import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'

export interface SearchSelectOption {
  value: string
  label: string
}

interface SearchSelectProps {
  value: string | null | undefined
  options: SearchSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  ariaLabel?: string
  disabled?: boolean
}

/** A Select you can type into. Same shape as TimezoneSelect, but over a plain
 *  {value, label} list rather than timezone tuples. */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  ariaLabel,
  disabled,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false)
  const current = options.find(o => o.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className="w-full cursor-pointer justify-between font-normal"
        >
          <span className={cn('truncate', !current && 'text-muted-foreground')}>
            {current ? current.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {options.map(option => (
              <CommandItem
                key={option.value}
                // cmdk searches the `value` prop, so the label goes there.
                value={option.label}
                onSelect={() => { onChange(option.value); setOpen(false) }}
                className="cursor-pointer"
              >
                <Check className={cn('mr-2 size-4', option.value === value ? 'opacity-100' : 'opacity-0')} />
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
